import { useMemo, useState } from "react";
import { Switch } from "@mui/material";
import styles from "./PricingReview.module.css";
import {
  buildReview,
  money,
  num,
  pct,
  quoteAt,
  symbolFor,
} from "./pricingModel";

/**
 * PLANS & PRICING — REVIEW
 * ========================
 * The screen a reviewer uses to decide whether a vendor's submitted pricing can
 * be published. Easexpense is merchant of record, so a price approved here is a
 * price a real buyer is charged.
 *
 * What it replaces
 * ----------------
 * The previous view (MarketPlaceT1/T2/T3, ~2,100 lines of near-duplicate JSX)
 * rendered pricing as a row of *marketing* pricing cards built from disabled
 * form inputs, one billing interval at a time. Concretely:
 *   • You picked a plan type, then picked ONE interval, and saw only that
 *     interval's plans. Comparing Basic-monthly with Basic-yearly meant
 *     clicking between two screens and remembering the numbers.
 *   • The carve-out — the entire Easexpense business model — was never shown.
 *     Marketplace price and vendor price sat in separate fields inside a modal
 *     behind a pencil icon; the spread was never computed anywhere.
 *   • Every field was a disabled <input>/<select>, so read-only data wore the
 *     visual weight of an editable form and the two genuinely editable things
 *     (marketplace price, "needs admin approval") were indistinguishable
 *     from the 15 things that were not.
 *   • Volume ("Full") and graduated ("Partial") pricing appeared as the bare
 *     words "Full" and "Partial" in a disabled dropdown. Those two words are
 *     different arithmetic and the screen never said which was which.
 *   • Nothing was validated. A plan priced at 0 with a vendor price of 500 —
 *     which exists in this database — rendered as calmly as a correct one.
 *
 * The structure here answers the reviewer's questions in the order they are
 * actually asked: what is proposed → what changed → is the margin right → is
 * anything over the cap → is anything nonsensical → can I publish.
 *
 * Research this leans on: Chargebee's price-point table (one price point per
 * frequency, so a missing one is a data error rather than a blank), Stripe's
 * tier-editor column names and its volume-vs-graduated worked example,
 * GitHub/Primer's rule that a diff must never be carried by colour alone,
 * Carbon's row-height scale, and Atlassian's warning-vs-error timing (a cap
 * breach is advance notice at load, not an error on click).
 */

/* ── icons ───────────────────────────────────────────────────────────── */
const svg = (d, key) => (
  <svg key={key} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
       strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const IcoOk = svg(<><circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>);
const IcoWarn = svg(<><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9v4.5M12 17h.01" /></>);
const IcoBlock = svg(<><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></>);
const IcoChevron = svg(<path d="m9 18 6-6-6-6" />);

const VERDICT = {
  ok: { cls: "verdictOk", icon: IcoOk, title: "Nothing blocking" },
  review: { cls: "verdictReview", icon: IcoWarn, title: "Needs a judgement call" },
  block: { cls: "verdictBlock", icon: IcoBlock, title: "Do not publish yet" },
};

const Badge = ({ kind, glyph, children }) => (
  <span className={`${styles.badge} ${styles[kind]}`}>
    {glyph && <span className={styles.glyph}>{glyph}</span>}
    {children}
  </span>
);

/**
 * old → new, in place. Struck-through old, full-weight new, an explicit arrow
 * and a ~/+ glyph so the change survives greyscale and colour-blindness —
 * Primer's rule, and the reason GitHub keeps its +/− gutter alongside the tint.
 */
function Diff({ diff, currency, fallback }) {
  if (!diff) return fallback;
  return (
    <span className={styles.diff}>
      <span className={styles.glyph} aria-hidden="true">~</span>
      <span className={styles.diffOld}>{money(diff.before, currency)}</span>
      <span className={styles.diffArrow} aria-hidden="true">→</span>
      <span className={styles.diffNew}>{money(diff.after, currency)}</span>
    </span>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

export default function PricingReview({
  planType = [],
  intervals = [],
  plans = [],
  features = [],
  setPlans,
  approvalState = {},
  setApprovalState,
  vendorId,
  ownerVendorId,
  changeJson = {},
  planChanges = {},
  featureMetaChanges = {},
  changeType,
  // "published" = the decision is already made and this is the live catalogue.
  source,
  approvalStatus,
  readOnly = false,
  priceDecisions = {},
  onPriceDecision,
  busy = false,
  onApprove,
  onReject,
  onShowVendor,
  headerExtra = null,
}) {
  // "published" means promote-on-approve already consumed the staging rows and
  // we are looking at the live catalogue — the screen becomes a record.
  const isDecided =
    source === "published" && approvalStatus && approvalStatus !== "reviewing";
  const [typeIdx, setTypeIdx] = useState(0);
  const [open, setOpen] = useState({}); // expanded ledger rows, keyed by plan id
  const [compact, setCompact] = useState(false);
  const [drafts, setDrafts] = useState({}); // `${planId}:${unitId}` -> string

  const review = useMemo(
    () =>
      buildReview({
        planType,
        intervals,
        plans,
        features,
        changeJson,
        planChanges,
        vendorId,
        ownerVendorId,
      }),
    [planType, intervals, plans, features, changeJson, planChanges, vendorId, ownerVendorId],
  );

  const section = review.sections[typeIdx] || review.sections[0];
  const canEditApprovalFlag = String(ownerVendorId) === String(vendorId);

  /* ── writes ──────────────────────────────────────────────────────────
     Both handlers below reproduce the legacy MarketPlaceT1/T2/T3 behaviour
     byte for byte, because `approvalState` is posted verbatim to
     /admin/approve_product. The publish payload must not change shape. */

  const saveUnitPrices = (product, unitPrices) => {
    setPlans((prevPlans) =>
      prevPlans.map((plan) => {
        if (plan.id !== product.id) return plan;
        const priceByUnitId = unitPrices.reduce((acc, unit) => {
          acc[unit.id] = unit.marketplace_price;
          return acc;
        }, {});
        return {
          ...plan,
          unit_ids: (plan.unit_ids || []).map((unit) =>
            Object.prototype.hasOwnProperty.call(priceByUnitId, unit.id)
              ? { ...unit, Marketplace_price: priceByUnitId[unit.id] }
              : unit,
          ),
        };
      }),
    );

    setApprovalState((prev) => ({
      ...prev,
      [product.id]: {
        need_easexpense_approval:
          prev[product.id]?.need_easexpense_approval ?? product.need_easexpense_approval,
        unit_prices: unitPrices.map((unit) => ({
          id: unit.id,
          marketplace_price: unit.marketplace_price,
        })),
      },
    }));
  };

  const toggleApproval = (product) => {
    setApprovalState((prev) => {
      const current =
        prev[product.id]?.need_easexpense_approval ?? product.need_easexpense_approval;
      const currentUnitPrices =
        prev[product.id]?.unit_prices ||
        (product.unit_ids || []).map((u) => ({
          id: u.id,
          marketplace_price: Number(u.Marketplace_price ?? 0),
        }));
      return {
        ...prev,
        [product.id]: {
          need_easexpense_approval: !current,
          unit_prices: currentUnitPrices,
        },
      };
    });
  };

  /** Commit one edited tier, carrying every other tier through unchanged. */
  const commitPrice = (plan, unitId) => {
    const key = `${plan.id}:${unitId}`;
    const raw = drafts[key];
    if (raw === undefined) return;
    setDrafts((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
    const value = raw === "" ? "" : Number(raw);
    saveUnitPrices(
      plan,
      (plan.unit_ids || []).map((u) => ({
        id: u.id,
        marketplace_price:
          String(u.id) === String(unitId) ? value : Number(u.Marketplace_price ?? 0),
      })),
    );
  };

  if (!review.sections.length) {
    return (
      <div className={styles.wrap}>
        <div className={styles.card}>
          {/* "Nothing was submitted" and "this was already decided" are very
              different things, and reading identically made a published
              listing look like data loss. */}
          <p className={styles.empty}>
            {approvalStatus && approvalStatus !== "reviewing"
              ? `This submission was ${approvalStatus.replace("_", " ")} and its pricing is no longer staged for review.`
              : "This submission has no plan types or pricing rows."}
          </p>
        </div>
      </div>
    );
  }

  const { counts, verdict } = review;
  const v = VERDICT[verdict];

  return (
    <div className={`${styles.wrap} ${compact ? styles.compact : ""}`}>
      {/* ── 1. Summary before detail ───────────────────────────────────
          Countable facts, so the reviewer knows where to look before the
          first scroll. GitHub's "N files changed" header, applied to prices. */}
      <div className={`${styles.verdict} ${styles[v.cls]}`}>
        <span className={styles.verdictMark}>{v.icon}</span>
        <span className={styles.verdictText}>
          <b className={styles.verdictTitle}>{v.title}</b>
          <span className={styles.verdictSub}>
            {verdict === "block"
              ? `${counts.blocking} problem${counts.blocking === 1 ? "" : "s"} would charge buyers the wrong amount.`
              : verdict === "review"
                ? `${counts.review} thing${counts.review === 1 ? "" : "s"} are legitimate but need you to agree to them.`
                : "Every price has a positive carve-out and sits inside the ±25%/year cap."}
          </span>
        </span>

        <span className={styles.stats}>
          <span className={styles.stat}>
            <b className={styles.statVal}>{counts.plans}</b>
            <span className={styles.statLbl}>Plans</span>
          </span>
          <span className={styles.stat}>
            <b className={styles.statVal}>{counts.periods}</b>
            <span className={styles.statLbl}>Periods</span>
          </span>
          <span className={styles.stat}>
            <b className={styles.statVal}>{counts.prices}</b>
            <span className={styles.statLbl}>Prices</span>
          </span>
          <span className={styles.stat}>
            <b className={`${styles.statVal} ${counts.changed ? styles.statInfo : styles.statMuted}`}>
              {counts.changed}
            </b>
            <span className={styles.statLbl}>Changed</span>
          </span>
          <span className={styles.stat}>
            <b className={`${styles.statVal} ${counts.overCap ? styles.statWarn : styles.statMuted}`}>
              {counts.overCap}
            </b>
            <span className={styles.statLbl}>Over cap</span>
          </span>
          <span className={styles.stat}>
            <b className={`${styles.statVal} ${counts.blocking ? styles.statDanger : styles.statOk}`}>
              {counts.blocking}
            </b>
            <span className={styles.statLbl}>Blocking</span>
          </span>
        </span>
      </div>

      {headerExtra}

      {/* ── 2. Plan type as a segment, not extra columns ────────────────
          Two plan types × 3 plans × 3 periods is 18 cells, past scannable.
          Change the axis, not the grid. */}
      {review.sections.length > 1 && (
        <div className={styles.segments}>
          {review.sections.map((s, i) => {
            const bad = s.checks.filter((c) => c.severity === "block").length;
            const warn = s.checks.filter((c) => c.severity === "review").length;
            return (
              <button
                key={s.typeIndex}
                type="button"
                onClick={() => setTypeIdx(i)}
                className={`${styles.segment} ${i === typeIdx ? styles.segmentOn : ""}`}
              >
                {s.name}
                <span className={styles.segmentTag}>
                  {s.rows.length}p × {s.periods.length}
                </span>
                {bad > 0 && <Badge kind="bDanger" glyph="!">{bad}</Badge>}
                {bad === 0 && warn > 0 && <Badge kind="bWarn" glyph="!">{warn}</Badge>}
              </button>
            );
          })}
        </div>
      )}

      {/* ── 3. What is being proposed: the plan × period grid ───────────
          The shape the data actually has. The backend stores one pricing row
          per (type, plan, period), so "Basic monthly" and "Basic yearly" are
          two rows that the old screen put on two different screens. */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>What is being proposed</h3>
          <Badge kind="bMuted">{section.name}</Badge>
          <span className={styles.spacer} />
          <span className={styles.statLbl}>Buyer-facing price per period</span>
        </div>
        <div className={styles.scroll}>
          <table className={styles.grid}>
            <thead>
              <tr>
                <th className={styles.gridRowHead}>Plan</th>
                {section.periods.map((p) => (
                  <th key={p.index} className={styles.gridCol}>{p.label}</th>
                ))}
                {/* Absorbs the leftover width so the priced columns stay
                    packed to the left instead of drifting apart. */}
                <th className={styles.gridFill} aria-hidden="true" />
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row) => {
                const priced = row.cells.filter((c) => !c.missing && c.perMonth > 0);
                const cheapestPerMonth = priced.length
                  ? Math.min(...priced.map((c) => c.perMonth))
                  : null;
                const baselinePerMonth = priced.length
                  ? priced.reduce((a, b) => (a.period.months <= b.period.months ? a : b)).perMonth
                  : null;
                return (
                  <tr key={row.name}>
                    <th scope="row" className={styles.gridRowHead}>{row.name}</th>
                    {row.cells.map((cell) => {
                      /* Every cell renders the SAME two slots — a primary
                         line and a reserved metadata line — whether or not it
                         has metadata to put in the second one. That is what
                         keeps ₹100 and ₹1,000 on one baseline when only the
                         yearly cell carries a "≡ ₹83.33/month" sub-line. */
                      if (cell.missing) {
                        return (
                          <td key={cell.period.index} className={styles.gridCell}>
                            <span className={styles.gridPrimary}>
                              <span className={styles.gridGap}>not offered</span>
                            </span>
                            <span className={styles.gridMeta} aria-hidden="true" />
                          </td>
                        );
                      }
                      if (cell.plan.price_type === "contact") {
                        return (
                          <td key={cell.period.index} className={styles.gridCell}>
                            <span className={styles.gridPrimary}>
                              <Badge kind="bInfo">Contact us</Badge>
                            </span>
                            <span className={styles.gridMeta} aria-hidden="true" />
                          </td>
                        );
                      }
                      const saving =
                        baselinePerMonth && cell.perMonth && cell.perMonth < baselinePerMonth
                          ? ((baselinePerMonth - cell.perMonth) / baselinePerMonth) * 100
                          : null;
                      return (
                        <td key={cell.period.index} className={styles.gridCell}>
                          <span className={styles.gridPrimary}>
                            <Diff
                              diff={cell.priceDiff}
                              currency={cell.currency}
                              fallback={
                                <span className={styles.gridPrice}>
                                  {money(cell.marketplace, cell.currency)}
                                </span>
                              }
                            />
                          </span>
                          <span className={styles.gridMeta}>
                            {cell.perMonth !== null && cell.period.months !== 1 && (
                              <>
                                ≡ {money(cell.perMonth, cell.currency)}/month
                                {saving !== null && cell.perMonth === cheapestPerMonth && (
                                  <span className={styles.gridSaving}>
                                    −{saving.toFixed(0)}%
                                  </span>
                                )}
                              </>
                            )}
                            {cell.tiers.length > 1 && (
                              <span className={styles.gridTiers}>
                                from · {cell.tiers.length} usage tiers
                              </span>
                            )}
                          </span>
                        </td>
                      );
                    })}
                    <td className={styles.gridFill} />
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className={styles.cardNote}>
          Yearly and quarterly prices are shown with their per-month equivalent so a
          column can be compared without arithmetic. “Not offered” means this plan has
          no pricing row for that period — usually an omission rather than a decision.
        </p>
      </div>

      {/* ── 4. The ledger: margin and the diff, one row per price ───────
          Chargebee's price-point table. Each price gets a full row, which is
          the only way marketplace / vendor / spread / spread% / change all fit
          without the reviewer holding numbers in their head. */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Prices &amp; carve-out</h3>
          {review.isReseller && <Badge kind="bInfo">Reseller — only this vendor's prices change</Badge>}
          {review.isHouseVendor && <Badge kind="bMuted">House vendor — no carve-out expected</Badge>}
          <span className={styles.spacer} />
          <button
            type="button"
            className={`${styles.act} ${styles.actQuiet} ${styles.actSmall}`}
            onClick={() => setCompact((c) => !c)}
          >
            {compact ? "Comfortable" : "Compact"}
          </button>
        </div>
        <div className={styles.scroll}>
          <table className={styles.ledger}>
            <thead>
              <tr>
                <th style={{ width: 34 }} aria-label="Expand" />
                <th>Plan</th>
                <th>Period</th>
                <th className={styles.num}>Buyer pays</th>
                <th className={styles.num}>Change</th>
                <th className={`${styles.num} ${styles.derived}`}>
                  <span className={styles.fx}>ƒ</span>Vendor gets
                </th>
                <th className={`${styles.num} ${styles.derived}`}>
                  <span className={styles.fx}>ƒ</span>Carve-out
                </th>
                <th className={`${styles.num} ${styles.derived}`}>
                  <span className={styles.fx}>ƒ</span>Margin
                </th>
                <th className={`${styles.num} ${styles.derived}`}>
                  <span className={styles.fx}>ƒ</span>≡ /month
                </th>
                <th>Flags</th>
                {/* PART-APPROVE. A submission is rarely all good or all bad —
                    four of six plans is the normal case — and rejecting the
                    whole thing told the vendor nothing about what to fix. */}
                {!readOnly && <th style={{ width: 92 }}>Publish?</th>}
              </tr>
            </thead>
            <tbody>
              {section.rows.map((row) => (
                <PlanGroup
                  key={row.name}
                  row={row}
                  section={section}
                  open={open}
                  setOpen={setOpen}
                  drafts={drafts}
                  setDrafts={setDrafts}
                  commitPrice={commitPrice}
                  toggleApproval={toggleApproval}
                  approvalState={approvalState}
                  canEditApprovalFlag={canEditApprovalFlag}
                  readOnly={readOnly}
                  priceDecisions={priceDecisions}
                  onPriceDecision={onPriceDecision}
                />
              ))}
            </tbody>
          </table>
        </div>
        <p className={styles.cardNote}>
          <span className={styles.fx}>ƒ</span> marks a derived column — vendor price,
          carve-out, margin and the per-month equivalent are computed, never typed.
          Easexpense is merchant of record: the carve-out is the spread between what
          the buyer pays and what the vendor is owed, not a mark-up added on top.
          <b> Buyer pays</b> is the one figure you can edit here; changing it re-derives
          the carve-out on publish.
        </p>
      </div>

      {/* ── 5. Is anything missing or nonsensical ───────────────────────
          Every check states the number, the limit and the gap — never a bare
          "exceeds policy". Blocking first; the list is ordered by severity. */}
      <div className={styles.card}>
        <div className={styles.cardHead}>
          <h3 className={styles.cardTitle}>Checks</h3>
          {counts.blocking > 0 && <Badge kind="bDanger" glyph="●">{counts.blocking} blocking</Badge>}
          {counts.review > 0 && <Badge kind="bWarn" glyph="●">{counts.review} to agree</Badge>}
          <span className={styles.spacer} />
          {review.sections.length > 1 && (
            <span className={styles.statLbl}>Across all plan types</span>
          )}
        </div>
        {/* Deliberately NOT scoped to the visible segment. Hiding a blocking
            problem behind a tab the reviewer has not clicked is exactly the
            failure the old screen had. */}
        <ChecksList
          checks={review.checks}
          showWhere={review.sections.length > 1}
        />
      </div>

      {/* ── 6. The feature table, kept but demoted ──────────────────────
          Real content, but nobody blocks a publish on it. It sits below the
          money and stays collapsed until asked for. */}
      {section.features.length > 0 && (
        <FeatureMatrix section={section} featureMetaChanges={featureMetaChanges} />
      )}

      {/* ── 7. Publish ─────────────────────────────────────────────────
          Sticky, so the decision is always reachable, and it tells you what is
          standing in the way rather than just greying out. */}
      {!readOnly && (
        <div className={styles.actionBar}>
          {onShowVendor && (
            <button
              type="button"
              className={`${styles.act} ${styles.actQuiet}`}
              onClick={onShowVendor}
              disabled={busy}
            >
              Vendor profile
            </button>
          )}
          <span className={styles.actionMsg}>
            {counts.blocking > 0 ? (
              <>
                <b>{counts.blocking} blocking problem{counts.blocking === 1 ? "" : "s"}</b> — approving
                publishes these prices to buyers as they stand.
              </>
            ) : counts.overCap > 0 ? (
              <>
                {counts.overCap} price{counts.overCap === 1 ? "" : "s"} move more than ±25%/year. Not a
                block — approve it if the vendor has a reason.
              </>
            ) : isDecided ? (
              // A decided submission has no future action to describe; beside
              // "Already approved" the old hint read as a contradiction.
              "Published to buyers. Prices here are the live catalogue."
            ) : changeType === "price_update" ? (
              "You'll be asked whether existing customers move to the new price."
            ) : (
              "Approving publishes this listing to buyers."
            )}
          </span>
          {/* Same three colours as the header, same meanings. onReject /
              onApprove are untouched — they still open the confirm dialog that
              owns the existing-customer options. */}
          {/* A decided submission is a record, not a decision. Live
              Approve/Reject on something already approved invites a reviewer to
              act twice on the same listing. */}
          {isDecided ? (
            <span className={styles.actQuiet}>
              Already {String(approvalStatus).replace("_", " ")} — this is the
              published pricing.
            </span>
          ) : (
            <>
              <button
                type="button"
                className={`${styles.act} ${styles.actStop}`}
                onClick={onReject}
                disabled={busy}
              >
                Reject
              </button>
              <button
                type="button"
                className={`${styles.act} ${styles.actGo}`}
                onClick={onApprove}
                disabled={busy}
              >
                Approve &amp; publish
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════ ledger rows ═══════════════════════════ */

/**
 * Publish this price, or not.
 *
 * Three states, but only two are a decision: unset means "publish, like
 * everything else", because a reviewer who approves a submission is saying yes
 * to all of it — that is how approval worked before part-approval existed and
 * it must stay the default. Turning one OFF is the act that needs a reason,
 * and the reason is the only thing the vendor can act on.
 */
function PriceVerdict({ planId, value, onChange }) {
  const rejected = value?.decision === "rejected";
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 12,
          fontWeight: 700,
          color: rejected ? "var(--danger-ink, #c53030)" : "inherit",
          cursor: "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={!rejected}
          onChange={(e) =>
            onChange?.(
              planId,
              e.target.checked ? null : { decision: "rejected", note: "" },
            )
          }
        />
        {rejected ? "Held back" : "Publish"}
      </label>
      {rejected && (
        <input
          value={value?.note || ""}
          onChange={(e) =>
            onChange?.(planId, { decision: "rejected", note: e.target.value })
          }
          placeholder="Why? The vendor sees this"
          aria-label="Reason this price was not approved"
          style={{
            width: 160,
            fontSize: 12,
            padding: "3px 6px",
            border: "1px solid var(--border-strong, #cbd5e1)",
            borderRadius: 6,
          }}
        />
      )}
    </div>
  );
}

function PlanGroup({
  row,
  open,
  setOpen,
  drafts,
  setDrafts,
  commitPrice,
  toggleApproval,
  approvalState,
  canEditApprovalFlag,
  readOnly,
  priceDecisions,
  onPriceDecision,
}) {
  const cells = row.cells.filter((c) => !c.missing);
  return (
    <>
      {row.cells.map((cell, i) => {
        const first = i === 0 ? styles.groupStart : "";
        if (cell.missing) {
          return (
            <tr key={`gap-${cell.period.index}`}
                className={`${styles.priceRow} ${styles.rowReview} ${first}`}>
              <td />
              <td className={styles.planName}>{row.name}</td>
              <td className={styles.periodName}>{cell.period.label}</td>
              <td colSpan={7}>
                <span className={styles.gridGap}>
                  no price for this period
                  {cells.length > 0 && " — priced for the others"}
                </span>
              </td>
            </tr>
          );
        }

        const worst = cell.checks.some((c) => c.severity === "block")
          ? styles.rowBlock
          : cell.checks.some((c) => c.severity === "review")
            ? styles.rowReview
            : cell.changed
              ? styles.rowChanged
              : "";
        const isOpen = Boolean(open[cell.plan.id]);
        const contact = cell.plan.price_type === "contact";

        return (
          <Row key={cell.plan.id}
               cell={cell} row={row} worst={`${worst} ${first}`} isOpen={isOpen}
               contact={contact} setOpen={setOpen} drafts={drafts} setDrafts={setDrafts}
               commitPrice={commitPrice} toggleApproval={toggleApproval}
               approvalState={approvalState} canEditApprovalFlag={canEditApprovalFlag}
               readOnly={readOnly}
               priceDecisions={priceDecisions} onPriceDecision={onPriceDecision} />
        );
      })}
    </>
  );
}

function Row({
  cell, row, worst, isOpen, contact, setOpen, drafts, setDrafts,
  commitPrice, toggleApproval, approvalState, canEditApprovalFlag, readOnly,
  priceDecisions, onPriceDecision,
}) {
  const plan = cell.plan;
  const unit = cell.unit;
  const key = `${plan.id}:${unit?.id}`;
  const draft = drafts[key];
  const edited = approvalState[plan.id]?.unit_prices !== undefined;

  return (
    <>
      <tr className={`${styles.priceRow} ${worst}`}>
        <td>
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${plan.name} ${cell.period.label}`}
            className={`${styles.disclose} ${isOpen ? styles.discloseOpen : ""}`}
            onClick={() => setOpen((o) => ({ ...o, [plan.id]: !o[plan.id] }))}
          >
            {IcoChevron}
          </button>
        </td>
        <td className={styles.planName}>{plan.name}</td>
        <td className={styles.periodName}>{cell.period.label}</td>

        {/* Buyer pays — the only editable figure, and it carries its own diff. */}
        <td className={styles.num}>
          {contact ? (
            <span className={styles.unchanged}>quoted</span>
          ) : readOnly ? (
            <Diff
              diff={cell.priceDiff}
              currency={cell.currency}
              fallback={<b>{money(cell.marketplace, cell.currency)}</b>}
            />
          ) : (
            <span className={styles.priceEdit}>
              <span className={styles.priceEditSym}>{symbolFor(cell.currency)}</span>
              <input
                type="number"
                step="0.01"
                className={styles.priceEditInput}
                aria-label={`Marketplace price for ${plan.name}, ${cell.period.label}`}
                value={draft !== undefined ? draft : (unit?.Marketplace_price ?? "")}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                onBlur={() => commitPrice(plan, unit?.id)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              />
            </span>
          )}
        </td>

        {/* Change — the number, and the cap breach stated with its gap. */}
        <td className={styles.num}>
          {cell.priceDelta !== null ? (
            <span
              className={`${styles.diffDelta} ${cell.priceDelta > 0 ? styles.deltaUp : styles.deltaDown}`}
            >
              {pct(cell.priceDelta)}
            </span>
          ) : cell.isNew ? (
            <Badge kind="bInfo" glyph="+">new</Badge>
          ) : (
            <span className={styles.unchanged}>—</span>
          )}
        </td>

        <td className={`${styles.num} ${styles.derived}`}>
          <Diff
            diff={cell.vendorDiff}
            currency={cell.currency}
            fallback={
              cell.vendor === null ? (
                <span className={styles.unchanged}>not set</span>
              ) : (
                money(cell.vendor, cell.currency)
              )
            }
          />
        </td>
        <td className={`${styles.num} ${styles.derived}`}>
          {cell.spread === null ? (
            <span className={styles.unchanged}>—</span>
          ) : cell.spread < 0 ? (
            <b style={{ color: "var(--danger-ink)" }}>{money(cell.spread, cell.currency)}</b>
          ) : (
            money(cell.spread, cell.currency)
          )}
        </td>
        <td className={`${styles.num} ${styles.derived}`}>
          {cell.spreadPct === null ? (
            <span className={styles.unchanged}>—</span>
          ) : (
            <b style={{ color: cell.spreadPct <= 0 ? "var(--danger-ink)" : undefined }}>
              {cell.spreadPct.toFixed(1)}%
            </b>
          )}
        </td>
        <td className={`${styles.num} ${styles.derived}`}>
          {cell.perMonth === null ? (
            <span className={styles.unchanged}>—</span>
          ) : (
            money(cell.perMonth, cell.currency)
          )}
        </td>

        <td>
          {cell.capFinding && <Badge kind="bWarn" glyph="!">over cap</Badge>}
          {cell.checks.some((c) => c.severity === "block") && (
            <Badge kind="bDanger" glyph="!">
              {cell.checks.filter((c) => c.severity === "block").length} blocking
            </Badge>
          )}
          {contact && <Badge kind="bInfo">contact</Badge>}
          {plan.status && plan.status !== "Active" && <Badge kind="bMuted">{plan.status}</Badge>}
          {edited && <span className={styles.edited}>edited</span>}
          {cell.tiers.length > 1 && (
            <Badge kind="bMuted">
              {cell.tiers.length} tiers · {plan.unit_price_type === "Full" ? "volume" : "graduated"}
            </Badge>
          )}
        </td>

        {/* The verdict on THIS price. Default is publish — a reviewer who
            opens the queue and approves is saying yes to everything, which is
            how it worked before. Turning one off keeps the live plan exactly
            as it is and asks for a reason the vendor will read. */}
        {!readOnly && (
          <td>
            <PriceVerdict
              planId={plan.id}
              value={priceDecisions?.[plan.id]}
              onChange={onPriceDecision}
            />
          </td>
        )}
      </tr>

      {isOpen && (
        <tr className={styles.detailRow}>
          <td colSpan={10}>
            <Detail
              cell={cell}
              row={row}
              drafts={drafts}
              setDrafts={setDrafts}
              commitPrice={commitPrice}
              toggleApproval={toggleApproval}
              approvalState={approvalState}
              canEditApprovalFlag={canEditApprovalFlag}
              readOnly={readOnly}
            />
          </td>
        </tr>
      )}
    </>
  );
}

/* ══════════════════════════════ expanded detail ═══════════════════════ */

const FIELDS = [
  ["description", "Description"],
  ["currency", "Currency"],
  ["unit_price_type", "Unit price type"],
  ["status", "Status"],
  ["region", "Region"],
  ["tags", "Tags"],
  ["trial_period_days", "Trial days"],
  ["modify_orders", "Mid-cycle changes"],
];

function Detail({
  cell, drafts, setDrafts, commitPrice, toggleApproval,
  approvalState, canEditApprovalFlag, readOnly,
}) {
  const plan = cell.plan;
  // Always show the price rows — even a single-price plan carries the
  // industry (list) price and HSN/SAC the admin has to confirm.
  const showTiers = cell.tiers.length > 0;

  return (
    <div className={styles.detail}>
      <div className={styles.detailGrid}>
        {FIELDS.map(([field, label]) => {
          const diff = cell.fieldDiffs[field];
          const value = Array.isArray(plan[field]) ? plan[field].join(", ") : plan[field];
          return (
            <div key={field} className={styles.field}>
              <span className={styles.fieldLbl}>{label}</span>
              <span className={styles.fieldVal}>
                {diff ? (
                  <span className={styles.diff}>
                    <span className={styles.glyph} aria-hidden="true">~</span>
                    <span className={styles.diffOld}>{diff.before}</span>
                    <span className={styles.diffArrow} aria-hidden="true">→</span>
                    <span className={styles.diffNew}>{diff.after}</span>
                  </span>
                ) : (
                  value || <em>not set</em>
                )}
              </span>
            </div>
          );
        })}
        <div className={styles.field}>
          <span className={styles.fieldLbl}>Price held for</span>
          <span className={styles.fieldVal}>
            {plan.n_price_validity_interval && plan.price_validity_interval ? (
              `${plan.n_price_validity_interval} ${plan.price_validity_interval}`
            ) : (
              <em>not set</em>
            )}
          </span>
        </div>
      </div>

      {Array.isArray(plan.plan_highlight) && plan.plan_highlight.filter(Boolean).length > 0 && (
        <div>
          <p className={styles.subHead}>Plan highlights</p>
          <span className={styles.fieldVal}>{plan.plan_highlight.filter(Boolean).join(" · ")}</span>
        </div>
      )}

      {/* ── tier bands ──────────────────────────────────────────────────
          Stripe's column names and Stripe's own worked example. The mode is
          spelled out as a sentence because "Full" and "Partial" carry no
          meaning outside our own schema, and the two are different maths. */}
      {showTiers && <Tiers cell={cell} drafts={drafts} setDrafts={setDrafts}
                           commitPrice={commitPrice} readOnly={readOnly} />}

      {/* The one other thing an admin can actually change here. Disabled for a
          reseller submission because the flag belongs to the owning vendor. */}
      <div className={styles.toggleRow}>
        <span className={styles.toggleLbl}>
          Require Easexpense approval for each order
          <span className={styles.toggleHint}>
            {canEditApprovalFlag
              ? "Buyers cannot self-serve this plan; every order waits for a human."
              : "Owned by the product's vendor — not editable from a reseller submission."}
          </span>
        </span>
        <Switch
          checked={Boolean(
            approvalState[plan.id]?.need_easexpense_approval ?? plan.need_easexpense_approval,
          )}
          onChange={() => toggleApproval(plan)}
          disabled={!canEditApprovalFlag || readOnly}
        />
      </div>
    </div>
  );
}

function Tiers({ cell, drafts, setDrafts, commitPrice, readOnly }) {
  const plan = cell.plan;
  const volume = plan.unit_price_type === "Full";
  const sample = useMemo(() => {
    const second = cell.tiers[1];
    const q = num(second?.StartRange);
    return q && q > 0 ? q : (num(cell.tiers[0]?.EndRange) || 10) + 1;
  }, [cell.tiers]);
  const quote = quoteAt(plan, sample);

  const multi = cell.tiers.length > 1;

  return (
    <div>
      <p className={styles.subHead}>
        {multi ? "Usage tiers" : "Unit pricing"}
        {multi && (
          <Badge kind={volume ? "bInfo" : "bMuted"}>
            {plan.unit_price_type || "not set"}
          </Badge>
        )}
      </p>

      {multi && (
      <div className={`${styles.mode} ${volume ? styles.modeVolume : styles.modeGraduated}`}>
        {volume ? (
          <>
            <b>Volume pricing.</b> The buyer's whole quantity is billed at the single
            rate of the band it lands in — <b>all</b> units at that band's rate, not a
            mix. Crossing into a cheaper band re-prices everything.
          </>
        ) : (
          <>
            <b>Graduated pricing.</b> Each band bills only its own slice: the first N
            units at the first rate, the next slice at the next rate, and so on. The
            buyer's total is the sum of the slices.
          </>
        )}
        {quote && (
          <div className={styles.worked}>
            Worked example at <b>{sample} units</b>: <code>{quote.workings}</code> ={" "}
            <b>{money(quote.total, cell.currency)}</b>
          </div>
        )}
      </div>
      )}

      <div className={styles.scroll}>
        <table className={styles.tierTable}>
          <thead>
            <tr>
              <th>First unit</th>
              <th>Last unit</th>
              <th>Step</th>
              <th>Per unit (buyer)</th>
              <th>Industry (list)</th>
              <th><span className={styles.fx}>ƒ</span>Vendor</th>
              <th><span className={styles.fx}>ƒ</span>Carve-out</th>
              <th>HSN / SAC</th>
              {multi && <th>Default</th>}
              <th>Min / Max</th>
            </tr>
          </thead>
          <tbody>
            {cell.tiers.map((t) => {
              const key = `${plan.id}:${t.id}`;
              const draft = drafts[key];
              const mk = num(t.Marketplace_price);
              const vd = num(t.Vendor_price);
              const diff = cell.unitDiffs?.[t.id]?.Marketplace_price;
              return (
                <tr key={t.id}>
                  <td>{num(t.StartRange) ?? "—"}</td>
                  <td>{num(t.EndRange) === null ? "∞" : num(t.EndRange)}</td>
                  <td>{num(t.Steps) ?? 1}</td>
                  <td>
                    {readOnly ? (
                      <Diff
                        diff={diff}
                        currency={cell.currency}
                        fallback={<b>{money(mk, cell.currency)}</b>}
                      />
                    ) : (
                      <span className={styles.priceEdit}>
                        <span className={styles.priceEditSym}>{symbolFor(cell.currency)}</span>
                        <input
                          type="number"
                          step="0.01"
                          className={styles.priceEditInput}
                          aria-label={`Rate for units ${num(t.StartRange)} and up`}
                          value={draft !== undefined ? draft : (t.Marketplace_price ?? "")}
                          onWheel={(e) => e.target.blur()}
                          onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                          onBlur={() => commitPrice(plan, t.id)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        />
                      </span>
                    )}
                  </td>
                  <td>
                    {num(t.Industry_price) ? (
                      <span title="Public list price, shown crossed out to buyers">
                        {money(t.Industry_price, cell.currency)}
                        {mk !== null && num(t.Industry_price) < mk && (
                          <span style={{ color: "var(--danger-ink)", marginLeft: 6 }}>
                            below buyer price
                          </span>
                        )}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{vd === null ? "—" : money(vd, cell.currency)}</td>
                  <td>
                    {mk === null || vd === null ? "—" : money(mk - vd, cell.currency)}
                  </td>
                  <td>{t.hsn_code ? <code>{t.hsn_code}</code> : <em>missing</em>}</td>
                  {multi && (
                    <td>{String(t.DefaultValue) === "true" ? "✓ Default" : "—"}</td>
                  )}
                  <td>
                    {num(t.Min_price) || num(t.Max_price)
                      ? `${money(t.Min_price, cell.currency)} – ${money(t.Max_price, cell.currency)}`
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════ checks ════════════════════════════════ */

const ORDER = { block: 0, review: 1, info: 2 };
const MARK = { block: "!", review: "!", info: "i" };
const SEV_LABEL = { block: "Blocking", review: "Agree", info: "Note" };

function ChecksList({ checks, showWhere = false }) {
  const sorted = useMemo(
    () => [...checks].sort((a, b) => ORDER[a.severity] - ORDER[b.severity]),
    [checks],
  );
  if (!sorted.length) {
    return (
      <div className={styles.clean}>
        <span className={`${styles.checkMark} ${styles.markInfo}`} style={{ background: "var(--ok-soft)", color: "var(--ok-ink)" }}>✓</span>
        Every price has a positive carve-out, every declared period is priced, and no
        tier bands overlap. Nothing to raise.
      </div>
    );
  }
  return (
    <ul className={styles.checks}>
      {sorted.map((c, i) => (
        <li
          key={`${c.code}-${i}`}
          className={`${styles.checkItem} ${c.severity === "block" ? styles.checkBlock : ""}`}
        >
          <span
            className={`${styles.checkMark} ${
              c.severity === "block"
                ? styles.markBlock
                : c.severity === "review"
                  ? styles.markReview
                  : styles.markInfo
            }`}
            aria-hidden="true"
          >
            {MARK[c.severity]}
          </span>
          <span className={styles.checkText}>
            <span
              className={`${styles.checkSev} ${
                c.severity === "block"
                  ? styles.sevBlock
                  : c.severity === "review"
                    ? styles.sevReview
                    : styles.sevInfo
              }`}
            >
              {SEV_LABEL[c.severity]}
            </span>
            {showWhere && c.where && <span className={styles.checkWhere}>{c.where}</span>}
            {c.text}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ══════════════════════════════ features ══════════════════════════════ */

function FeatureMatrix({ section, featureMetaChanges }) {
  const [show, setShow] = useState(false);
  const planCols = useMemo(() => {
    // One column per distinct plan name, taking the first priced instance —
    // the comparison table is per plan, not per plan × period.
    const seen = new Map();
    section.rows.forEach((r) => {
      const cell = r.cells.find((c) => !c.missing);
      if (cell) seen.set(r.name, cell.plan);
    });
    return Array.from(seen.entries());
  }, [section]);

  const rowCount = section.features.reduce((n, f) => n + (f.data?.length || 0), 0);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>Feature comparison</h3>
        <Badge kind="bMuted">
          {section.features.length} group{section.features.length === 1 ? "" : "s"} · {rowCount} rows
        </Badge>
        <span className={styles.spacer} />
        <button
          type="button"
          className={`${styles.act} ${styles.actQuiet} ${styles.actSmall}`}
          onClick={() => setShow((s) => !s)}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
      {show && (
        <div className={styles.scroll}>
          <table className={styles.featTable}>
            <thead>
              <tr>
                <th style={{ minWidth: 220 }}>Feature</th>
                {planCols.map(([name]) => (
                  <th key={name}>{name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.features.map((group) => (
                <FeatureGroup
                  key={group.key}
                  group={group}
                  planCols={planCols}
                  meta={featureMetaChanges?.[group.key]}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FeatureGroup({ group, planCols, meta }) {
  return (
    <>
      <tr className={styles.featGroup}>
        <td colSpan={planCols.length + 1}>
          {meta?.fieldDiffs?.name ? (
            <span className={styles.diff}>
              <span className={styles.diffOld}>{meta.fieldDiffs.name.before}</span>
              <span className={styles.diffArrow}>→</span>
              <span className={styles.diffNew}>{meta.fieldDiffs.name.after}</span>
            </span>
          ) : (
            group.name
          )}
        </td>
      </tr>
      {(group.data || []).map((title, i) => (
        <tr key={`${group.key}-${i}`}>
          <td className={styles.featRowHead}>{title}</td>
          {planCols.map(([name, plan]) => {
            const pf = (plan.features || []).find((f) => f.key === group.key);
            const value = pf?.data?.[i];
            return (
              <td key={name}>
                {value === undefined || value === "" ? (
                  <em style={{ color: "var(--faint)" }}>—</em>
                ) : (
                  String(value)
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}
